import React from 'react';
import {
  LightBulbIcon,
  NewspaperIcon,
  TrophyIcon,
  MegaphoneIcon,
  ChartBarIcon,
  GiftIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface QuickActionsProps {
  onSelectTemplate: (template: any) => void;
  onQuickSend: (template: any) => void;
}

const quickTemplates = {
  quiz: {
    title: 'Quick Quiz',
    type: 'quiz',
    priority: 'normal',
    category: 'quiz',
    body: 'Who is the current President of India?',
    quiz_data: {
      questions: [{
        id: Date.now().toString(),
        question: 'Who is the current President of India?',
        type: 'multiple_choice',
        options: ['Droupadi Murmu', 'Ram Nath Kovind', 'Pranab Mukherjee', 'APJ Abdul Kalam'],
        correctAnswer: 0,
        explanation: 'Droupadi Murmu is the 15th and current President of India, having taken office on July 25, 2022.',
        points: 10,
        timeLimit: 30,
      }]
    },
    targeting: {
      target_type: 'all',
      test_mode: false,
    },
  },
  news: {
    title: 'Breaking News Update',
    type: 'news',
    priority: 'high',
    category: 'current_affairs',
    body: 'Latest UPSC CSE 2024 notification released. Check important dates and eligibility criteria.',
    image_url: '',
    targeting: {
      target_type: 'all',
      test_mode: false,
    },
  },
  achievement: {
    title: '🎉 Congratulations!',
    type: 'achievement',
    priority: 'normal',
    category: 'milestone',
    body: 'You have completed 100 quiz questions! Keep up the great work!',
    actions: [{
      id: 'share',
      title: 'Share Achievement',
      style: 'primary',
    }],
    targeting: {
      target_type: 'segment',
      segment: 'active_7d',
      test_mode: false,
    },
  },
  announcement: {
    title: '📢 Important Announcement',
    type: 'info',
    priority: 'high',
    category: 'announcement',
    body: 'New study materials added for Indian Polity. Access them in the Subjects section.',
    actions: [{
      id: 'view',
      title: 'View Materials',
      style: 'primary',
    }],
    targeting: {
      target_type: 'all',
      test_mode: false,
    },
  },
  promotion: {
    title: '🎁 Special Offer!',
    type: 'promotion',
    priority: 'normal',
    category: 'offer',
    body: 'Get 30% off on Premium subscription. Limited time offer!',
    image_url: '',
    actions: [{
      id: 'claim',
      title: 'Claim Offer',
      style: 'success',
    }],
    targeting: {
      target_type: 'segment',
      segment: 'inactive',
      test_mode: false,
    },
  },
};

const QuickActions: React.FC<QuickActionsProps> = ({ onSelectTemplate, onQuickSend }) => {
  const templates = [
    {
      key: 'quiz',
      icon: LightBulbIcon,
      label: 'Quick Quiz',
      color: 'blue',
      description: 'Send a quiz question',
    },
    {
      key: 'news',
      icon: NewspaperIcon,
      label: 'News Update',
      color: 'purple',
      description: 'Share latest news',
    },
    {
      key: 'achievement',
      icon: TrophyIcon,
      label: 'Achievement',
      color: 'yellow',
      description: 'Celebrate milestones',
    },
    {
      key: 'announcement',
      icon: MegaphoneIcon,
      label: 'Announcement',
      color: 'red',
      description: 'Important updates',
    },
    {
      key: 'promotion',
      icon: GiftIcon,
      label: 'Promotion',
      color: 'pink',
      description: 'Special offers',
    },
  ];

  const handleQuickAction = (key: string, isQuickSend: boolean) => {
    const template = quickTemplates[key as keyof typeof quickTemplates];
    if (template) {
      if (isQuickSend) {
        onQuickSend(template);
        toast.success(`Sending ${key} notification...`);
      } else {
        onSelectTemplate(template);
        toast.success(`Template loaded: ${template.title}`);
      }
    }
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; hover: string; border: string }> = {
      blue: {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        text: 'text-blue-600 dark:text-blue-400',
        hover: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
        border: 'border-blue-200 dark:border-blue-800',
      },
      purple: {
        bg: 'bg-purple-50 dark:bg-purple-900/20',
        text: 'text-purple-600 dark:text-purple-400',
        hover: 'hover:bg-purple-100 dark:hover:bg-purple-900/30',
        border: 'border-purple-200 dark:border-purple-800',
      },
      yellow: {
        bg: 'bg-yellow-50 dark:bg-yellow-900/20',
        text: 'text-yellow-600 dark:text-yellow-400',
        hover: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30',
        border: 'border-yellow-200 dark:border-yellow-800',
      },
      red: {
        bg: 'bg-red-50 dark:bg-red-900/20',
        text: 'text-red-600 dark:text-red-400',
        hover: 'hover:bg-red-100 dark:hover:bg-red-900/30',
        border: 'border-red-200 dark:border-red-800',
      },
      green: {
        bg: 'bg-green-50 dark:bg-green-900/20',
        text: 'text-green-600 dark:text-green-400',
        hover: 'hover:bg-green-100 dark:hover:bg-green-900/30',
        border: 'border-green-200 dark:border-green-800',
      },
      pink: {
        bg: 'bg-pink-50 dark:bg-pink-900/20',
        text: 'text-pink-600 dark:text-pink-400',
        hover: 'hover:bg-pink-100 dark:hover:bg-pink-900/30',
        border: 'border-pink-200 dark:border-pink-800',
      },
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Quick Actions
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Click to load template • Double-click to send
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {templates.map((template) => {
          const Icon = template.icon;
          const colors = getColorClasses(template.color);

          return (
            <div
              key={template.key}
              className={`
                relative group cursor-pointer rounded-xl border-2 p-4 transition-all
                ${colors.bg} ${colors.border} ${colors.hover}
              `}
              onClick={() => handleQuickAction(template.key, false)}
              onDoubleClick={() => handleQuickAction(template.key, true)}
            >
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-lg ${colors.bg}`}>
                  <Icon className={`w-6 h-6 ${colors.text}`} />
                </div>
                <div className="flex-1">
                  <h4 className={`font-semibold ${colors.text}`}>
                    {template.label}
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {template.description}
                  </p>
                </div>
              </div>

              {/* Quick send indicator */}
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-xs bg-gray-900 dark:bg-gray-700 text-white px-2 py-1 rounded-full">
                  Double-click to send
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile-friendly info */}
      <div className="lg:hidden bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
        <p className="text-xs text-blue-600 dark:text-blue-400">
          💡 Tip: Tap any card to load the template, then customize and send
        </p>
      </div>
    </div>
  );
};

export default QuickActions;